import { SortingState } from "@tanstack/react-table";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { createSelector } from "reselect";

import { Axios } from "api/axios";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import { UserActionMenu } from "Components/MenuActionButton/UserActionMenu";
import SearchBox from "Components/SearchBox/SearchBox";
import { app_modules } from "helpers/apps_modules_list";
import { toast } from "react-toastify";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import {
  fetchUser as onfetchUser,
  updateStatusInUsers,
  UsersxportsCSV,
} from "../../../slices/users/thunk";
import OrganisationsTableContainer from "./UserTable";

const UserList = () => {
  document.title = "User List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const selectorganisation = createSelector(
    (state: any) => state.users,
    (Org: any) => ({
      usersList: Org.usersList,
      rowCount: Org.rowCount,
      loading: Org.loading,
      error: Org.error,
    })
  );

  const { usersList, rowCount, loading, error } =
    useSelector(selectorganisation);

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.user_management?.app_name
  );
  const user_master_permissions = master_permissions?.modules_permissions?.find(
    (item: any) =>
      item?.modules_name === app_modules?.user_management?.modules?.user
  );
  //console.log(user_master_permissions,"user_permissions")

  //*// For redirecting back to this page and get the same paginated pageIndex
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page_users") || "0");

  const [pageIndex, setPageIndex] = useState(pageParam);

  // Save current page to sessionStorage when component mounts
  useEffect(() => {
    if (pageParam > 0) {
      sessionStorage.setItem("usersPage", pageParam.toString());
    } else {
      // Clear sessionStorage when navigating from sidebar (page 0)
      sessionStorage.removeItem("usersPage");
    }
  }, [pageParam]);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<any>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [colValue, setColValue] = useState<any>();
  const [filterOptions, setFilterOptions] = useState<Record<string, any[]>>({});
  const [dropdownData, setDropDown] = useState<any>([]);

  // Password Modal State
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordModalMode, setPasswordModalMode] = useState<
    "web-sync" | "user"
  >("web-sync");
  const [selectedUserForPassword, setSelectedUserForPassword] =
    useState<any>(null);

  // Check if user is Admin
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const isAdmin = authUser?.user_type === "Admin";

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  //*// Initial data fetch
  useEffect(() => {
    // console.log(columnFilters, "columns filters applied sucessfully");
    dispatch(
      onfetchUser({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: columnFilters,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    // columnFilters,
    // colValue,
    sortField,
    sortDirection,
  ]);

  const prevFilters = useRef(columnFilters);

  useEffect(() => {
    if (JSON.stringify(prevFilters.current) !== JSON.stringify(columnFilters)) {
      prevFilters.current = columnFilters;
      dispatch(
        onfetchUser({
          page: 1, // Always use page 1 when filters change
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: columnFilters,
        })
      );
    }
  }, [columnFilters]);

  // Sync pageIndex with URL parameters
  useEffect(() => {
    const currentPageParam = parseInt(searchParams.get("page_users") || "0");
    if (currentPageParam !== pageIndex) {
      setPageIndex(currentPageParam);
    }
  }, [searchParams, pageIndex]);

  //*// All pagination Issues(resolved) when redirecting from the edit or view page
  // Reset pagenumber to 0 when not first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handlePageChange(0);
  }, [sorting, pageSize, columnFilters, search]);
  const handlePageChange = (newPage: number) => {
    setPageIndex(newPage);
    setSearchParams({ page_users: newPage.toString() });
    // Save current page to sessionStorage for back navigation
    sessionStorage.setItem("usersPage", newPage.toString());
  };
  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  //*//

  // Fetch permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const result = await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // toast.error("Failed to fetch latest permissions");
      }
    };
    fetchPermissions();
  }, [dispatch]);

  // Handle status toggle
  interface UserStatusRow {
    user_id?: number | string;
    id?: number | string;
    [key: string]: any;
  }

  // Get the logged-in user's ID
  const currentUserId = user?.user_id;

  const handleStatusToggle = async (org: UserStatusRow) => {
    // Check if user is trying to deactivate themselves
    const rowUserId = org.user_id || org.id;
    const isCurrentUser =
      currentUserId && rowUserId && String(currentUserId) === String(rowUserId);
    const isDeactivating = org.status === true;

    if (isCurrentUser && isDeactivating) {
      toast.error("You cannot deactivate your own account");
      return;
    }

    const updatedStatus = org.status === true ? false : true;
    const update_payload = { ...org, status: updatedStatus };

    try {
      await dispatch(updateStatusInUsers(update_payload)).unwrap();
      toast.success(
        `User ${updatedStatus ? "activated" : "deactivated"} successfully`
      );

      dispatch(
        onfetchUser({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: columnFilters,
        })
      );
    } catch (err: any) {}
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  
  // Custom Scrollable Tooltip Component
  const ScrollableTooltip = ({
    id,
    content,
    children,
    placement = "auto",
  }: {
    id: string;
    content: string;
    children: React.ReactElement;
    placement?: string;
  }) => {
    const [show, setShow] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setShow(true);
    };

    const handleMouseLeave = () => {
      timeoutRef.current = setTimeout(() => {
        setShow(false);
      }, 200);
    };

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <OverlayTrigger
        show={show}
        placement={placement as any}
        flip={true}
        popperConfig={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 4],
              },
            },
            {
              name: "preventOverflow",
              options: {
                boundary: "viewport",
                padding: 8,
              },
            },
            {
              name: "flip",
              options: {
                fallbackPlacements: ["top", "bottom", "left", "right"],
              },
            },
          ],
        }}
        overlay={
          <Tooltip
            id={id}
            className="light-tooltip"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div
              style={{
                whiteSpace: "pre-wrap",
                maxWidth: "400px",
                maxHeight: "300px",
                overflowY: "auto",
                wordBreak: "break-word",
              }}
            >
              {content}
            </div>
          </Tooltip>
        }
      >
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ display: "inline-block", width: "100%" }}
        >
          {children}
        </div>
      </OverlayTrigger>
    );
  };

  // const handleExport = async () => {
  //   const resultAction = await dispatch(UsersxportsCSV())

  //   if (exportUsersCSV.fulfilled.match(resultAction)) {
  //     const { blob, filename } = resultAction.payload

  //     const url = window.URL.createObjectURL(new Blob([blob]))
  //     const link = document.createElement('a')
  //     link.href = url
  //     link.setAttribute('download', filename)
  //     document.body.appendChild(link)
  //     link.click()
  //     link.remove()
  //     window.URL.revokeObjectURL(url)

  //     toast.success('CSV exported successfully')
  //   } else {
  //     toast.error('Failed to export CSV')
  //   }
  // }

  const handleExport = async () => {
    try {
      const { blob, filename } = await UsersxportsCSV({
        search,
        filters: columnFilters,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  const handleEdit = useCallback(
    (data: any) => {
      console.log(data, "data in handleEdit");
      navigate(`/user-form/`, {
        state: { user_data: data },
      });
    },
    [navigate]
  );

  const handleOpenPasswordModal = () => {
    setPassword("");
    setShowPassword(false);
    setPasswordError("");
    setPasswordModalMode("web-sync");
    setSelectedUserForPassword(null);
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setPassword("");
    setShowPassword(false);
    setPasswordError("");
    setPasswordModalMode("web-sync");
    setSelectedUserForPassword(null);
    setShowPasswordModal(false);
  };

  const handleUpdatePassword = async () => {
    if (!password.trim()) {
      toast.error("Please enter a password");
      return;
    }

    setPasswordError("");
    setIsSubmittingPassword(true);
    try {
      if (passwordModalMode === "user" && selectedUserForPassword) {
        // Update user password
        const userId =
          selectedUserForPassword.user_id || selectedUserForPassword.id;

        // Prepare payload with all required fields + password
        // user_type should be the ID (UUID) - handle both object and ID formats
        let userTypeId = "";
        if (selectedUserForPassword.user_type) {
          if (typeof selectedUserForPassword.user_type === "object") {
            // If it's an object, get the ID
            userTypeId =
              selectedUserForPassword.user_type.user_type_id ||
              selectedUserForPassword.user_type.id ||
              "";
          } else {
            // If it's already an ID (string/UUID)
            userTypeId = selectedUserForPassword.user_type;
          }
        }

        const payload = {
          username: selectedUserForPassword.username || "",
          mobile_no: selectedUserForPassword.mobile_no || "",
          email: selectedUserForPassword.email || "",
          user_type: userTypeId,
          first_name: selectedUserForPassword.first_name || "",
          last_name: selectedUserForPassword.last_name || "",
          password: password, // New password
        };

        await Axios.put(`api/user/edit/${userId}/`, payload);
        toast.success("User password updated successfully");
        handleClosePasswordModal();

        // Refresh the user list
        dispatch(
          onfetchUser({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: columnFilters,
          })
        );
      } else {
        // Update web sync password
        await Axios.post("api/item/set-password/", {
          password: password,
        });
        toast.success("Web sync password updated successfully");
        handleClosePasswordModal();
      }
    } catch (error: any) {
      console.error("Failed to update password:", error);

      // Handle Django REST Framework validation errors
      if (
        error?.response?.data?.password &&
        Array.isArray(error.response.data.password)
      ) {
        const passwordValidationError = error.response.data.password[0];
        setPasswordError(passwordValidationError);
        toast.error(passwordValidationError);
      } else {
        // Handle other error formats
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.response?.data?.errors ||
          "Failed to update password. Please try again.";
        toast.error(errorMessage);
        setPasswordError(errorMessage);
      }
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const handleUserPasswordUpdate = useCallback((userData: any) => {
    setPassword("");
    setShowPassword(false);
    setPasswordError("");
    setPasswordModalMode("user");
    setSelectedUserForPassword(userData);
    setShowPasswordModal(true);
  }, []);

  const handleViewDetails = useCallback((userData: any) => {
    const userId = userData.user_id || userData.id;
    navigate(`/user-details/${userId}`);
  }, [navigate]);

  const DynamicAction = useMemo(
    () => [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <UserActionMenu
            row={row}
            handleEdit={handleEdit}
            handleUpdatePassword={handleUserPasswordUpdate}
            handleViewDetails={handleViewDetails}
            canEdit={!!user_master_permissions?.permissions?.Update}
          />
        ),
      },
    ],
    [
      user_master_permissions?.permissions?.Update,
      handleEdit,
      handleUserPasswordUpdate,
      handleViewDetails,
    ]
  );
  // Table columns
  const columns = useMemo(
    () => [
      // {
      //   header: "Sr. No.",
      //   id: "sr_no", // important: since there's no accessorKey
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   cell: ({ row }: { row: any }) => {
      //     return row.index + 1 + pageIndex * pageSize;
      //   },
      // },
      {
        header: "First Name",
        accessorKey: "first_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/user/column-filter-dropdown/?column_filter=first_name",
        cell: ({ row }: { row: any }) => {
          return row.original.first_name || "-";
        },
      },
      {
        header: "Last Name",
        accessorKey: "last_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/user/column-filter-dropdown/?column_filter=last_name",
        cell: ({ row }: { row: any }) => {
          return row.original.last_name || "-";
        },
      },
      {
        header: "User Name",
        accessorKey: "username",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/user/column-filter-dropdown/?column_filter=username",
        cell: ({ row }: { row: any }) => {
          return row.original.username || "-";
        },
      },

      {
        header: "Type",
        accessorKey: "user_type__user_type",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/user/column-filter-dropdown/?column_filter=user_type__user_type",
        cell: ({ row }: { row: any }) => {
          return row.original.user_type__user_type || "-";
        },
      },
      // {
      //   header: 'Designation',
      //   accessorKey: 'designation',
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: 'dropdown' as const,
      //    filterFetchUrl: "api/user/column-filter-dropdown/?column_filter=designation",
      // },
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "api/user/column-filter-dropdown/?column_filter=email",
        cell: ({ row }: { row: any }) => {
          return row.original.email || "-";
        },
      },
      {
        header: "Mobile No",
        accessorKey: "mobile_no",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/user/column-filter-dropdown/?column_filter=mobile_no",
        cell: ({ row }: { row: any }) => {
          return row.original.mobile_no || "-";
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const usersStatus = row.original;
          const rowUserId = usersStatus.user_id || usersStatus.id;
          const isCurrentUser =
            currentUserId &&
            rowUserId &&
            String(currentUserId) === String(rowUserId);
          const isDeactivatingDisabled =
            isCurrentUser && usersStatus.status === true;

          return (
            <div
              style={{
                fontSize: "1.5rem",
                cursor: isDeactivatingDisabled ? "not-allowed" : "pointer",
                opacity: isDeactivatingDisabled ? 0.6 : 1,
              }}
              onClick={() => {
                if (!isDeactivatingDisabled) {
                  handleStatusToggle(usersStatus);
                }
              }}
              title={
                isDeactivatingDisabled
                  ? "You cannot deactivate your own account"
                  : ""
              }
            >
              <i
                className={`bi ${
                  usersStatus.status
                    ? "bi-toggle-on text-primary"
                    : "bi-toggle-off text-danger"
                }`}
                style={{ color: "#0073BB" }}
              />
            </div>
          );
        },
      },

      // {
      //   header: "Team",
      //   accessorKey: "team__team_name",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl:
      //     "api/user/column-filter-dropdown/?column_filter=team__team_name",
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.team__team_name || "-";
      //   },
      // },
      // {
      //   header: "Department",
      //   accessorKey: "department__department_name",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl:
      //     "api/user/column-filter-dropdown/?column_filter=department__department_name",
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.department__department_name || "-";
      //   },
      // },
      // {
      //   header: "Organisation",
      //   accessorKey: "organization__organization_name",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl:
      //     "api/user/column-filter-dropdown/?column_filter=organization__organization_name",
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.organization__organization_name || "-";
      //   },
      // },
      {
        header: "Address",
        accessorKey: "address",
        enableSorting: false,
        enableColumnFilter: false,
        minSize: 300,
        size: 300,
        cell: ({ row }: { row: any }) => {
          const address = row.original.address || "-";
          const hasLongText =
            address.length > 100 ||
            (address !== "-" && address.split("\n").length > 4);

          const cellContent = (
            <div style={{ minWidth: "300px", maxWidth: "400px" }}>
              <div
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {address}
              </div>
            </div>
          );

          // Show tooltip for long text
          if (hasLongText && address !== "-") {
            return (
              <ScrollableTooltip
                id={`tooltip-address-${row.original.user_id || row.original.id}`}
                content={address}
                placement="auto"
              >
                {cellContent}
              </ScrollableTooltip>
            );
          }

          return cellContent;
        },
      },
      {
        header: "City",
        accessorKey: "city",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "api/user/column-filter-dropdown/?column_filter=city",
        cell: ({ row }: { row: any }) => {
          return row.original.city || "-";
        },
      },
      {
        header: "State",
        accessorKey: "state",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "api/user/column-filter-dropdown/?column_filter=state",
        cell: ({ row }: { row: any }) => {
          return row.original.state || "-";
        },
      },
      {
        header: "Country",
        accessorKey: "country",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/user/column-filter-dropdown/?column_filter=country",
        cell: ({ row }: { row: any }) => {
          return row.original.country || "-";
        },
      },
      {
        header: "Pincode",
        accessorKey: "pincode",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/user/column-filter-dropdown/?column_filter=pincode",
        cell: ({ row }: { row: any }) => {
          return row.original.pincode || "-";
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_name ?? "N/A";
          const date = formatDate(row.original.created_at);
          return `  ${name}  -  ${date}  `; // extra spaces for visual padding
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_name ?? "N/A";
          const date = formatDate(row.original.updated_at);
          return `  ${name}  -  ${date}  `; // spaces before/after the name
        },
      },
      ...DynamicAction,
    ],
    [
      dispatch,
      usersList,
      handleStatusToggle,
      user_master_permissions?.permissions,
      currentUserId,
    ]
  );

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">User</h6>
                    </Col>

                    {/* Search Input Box (reusable)*/}
                    <Col xl={2} md={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        <Button
                          className="btn btn-subtle-danger d-none"
                          id="remove-actions"
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>

                        {
                          <Button
                            variant="secondary"
                            onClick={handleOpenPasswordModal}
                            className="btn btn-secondary"
                          >
                            <i className="bi bi-key align-baseline me-1"></i>
                            Edit Web Sync Password
                          </Button>
                        }

                        {user_master_permissions?.permissions?.Create ===
                          true && (
                          <Link to="/user-form" className="btn btn-primary">
                            <i className="bi bi-plus-circle align-baseline me-1"></i>
                            Create User
                          </Link>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <OrganisationsTableContainer
                      columns={columns}
                      data={usersList && usersList.length > 0 ? usersList : []}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      // onPageChange={setPageIndex}
                      onPageChange={handlePageChange} // for persisted pagination across pages
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      colValue={setColValue}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      manualSorting={true}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      //filterOptions={filterOptions}
                      SearchPlaceholder="Search organisation..."
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Password Modal (Web Sync or User Password) */}
      <Modal
        show={showPasswordModal}
        onHide={handleClosePasswordModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {passwordModalMode === "user"
              ? "Update User Password"
              : "Edit Web Sync Password"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {passwordModalMode === "user" && selectedUserForPassword && (
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Username:</strong>{" "}
                {selectedUserForPassword.username || "-"}
              </Form.Label>
            </Form.Group>
          )}
          <Form.Group>
            <Form.Label>New Password</Form.Label>
            <div style={{ position: "relative" }}>
              <Form.Control
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  // Clear error when user starts typing
                  if (passwordError) {
                    setPasswordError("");
                  }
                }}
                onKeyPress={(e) => {
                  if (
                    e.key === "Enter" &&
                    password.trim() &&
                    !isSubmittingPassword
                  ) {
                    e.preventDefault();
                    handleUpdatePassword();
                  }
                }}
                style={{ paddingRight: "40px" }}
                autoComplete="new-password"
                name="web-sync-password"
                id="web-sync-password"
                isInvalid={!!passwordError}
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowPassword(!showPassword);
                }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0",
                  color: "#6c757d",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                tabIndex={-1}
              >
                <i
                  className={!showPassword ? "bi bi-eye-slash" : "bi bi-eye"}
                  style={{ fontSize: "1.1rem" }}
                ></i>
              </button>
            </div>
            {passwordError && (
              <Form.Control.Feedback
                type="invalid"
                style={{ display: "block" }}
              >
                {passwordError}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClosePasswordModal}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!password.trim() || isSubmittingPassword}
            onClick={(e) => {
              e.preventDefault();
              handleUpdatePassword();
            }}
          >
            {isSubmittingPassword ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              "Update Password"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default UserList;
